import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import FeatureHeader from './FeatureHeader';
import Tile from './Tile';
import { useReveal } from './useReveal';
import BuffyPeek from './BuffyPeek';
import './features.css';

/* F2 "Assessments built for the SAT." (PAGE STRUCTURE v3, 2026-09-29).
   One wallpaper tile (the soft mint mesh the owner liked; lead 2026-09-29:
   calm, in the blue-green-purple family, not F1's violet) with two white
   cards:
   - a student's view: ONE SAT-style Math item (Advanced Math) with four
     choices; the student picks a WRONG answer, A (-5), shown as a plain
     selection (a student never sees right or wrong), then presses Next;
   - the teacher's view it feeds: the class's average level (1 to 7) in all
     8 SAT domains, grouped Math / Reading & Writing, as segmented bars; the
     lowest domain (Advanced Math, the same domain as the question) is
     highlighted.
   Side by side (tile 680px+), a STRAIGHT connector with dot ends (the
   page's row-to-row style, measured from the DOM) runs from the picked
   choice to the Advanced Math row: this miss shows up there. The two cards
   share one bar height and the choice list is set so choice A and the
   Advanced Math row share a center line, so the line is one flat run; if
   they ever differ it draws a square elbow, never a curve. Narrower, the
   cards stack and the connector is dropped.

   Plays once on scroll-in at the page's slow pace (CSS scales every --d by
   --cxs-slow = 1.35): the cards rise in, B is picked, Next presses, the
   connector draws, the bars fill one by one, then the lowest row lights up.
   ?settled=1 and reduced motion show the final frame.

   Sample data (SAMPLE DATA CANON, DECISIONS.md). Math checked:
   f(x) = x^2 - 4x, f(5) = 25 - 20 = 5 (B); the student picks A (-5), the
   subtraction turned around (20 - 25). Minus signs are U+2212. The Advanced
   Math class average (2.8) is F4's starting point. */

const MINUS = '−';
const CHOICES = [
  { key: 'A', value: `${MINUS}5`, picked: true },
  { key: 'B', value: '5' },
  { key: 'C', value: '20' },
  { key: 'D', value: '45' },
];
const GROUPS = [
  {
    name: 'Math',
    rows: [
      { name: 'Algebra', level: 4.2 },
      { name: 'Advanced Math', level: 2.8, weak: true },
      { name: 'Problem Solving & Data Analysis', level: 3.9 },
      { name: 'Geometry & Trigonometry', level: 3.4 },
    ],
  },
  {
    name: 'Reading & Writing',
    rows: [
      { name: 'Information & Ideas', level: 4.5 },
      { name: 'Craft & Structure', level: 3.8 },
      { name: 'Expression of Ideas', level: 4.1 },
      { name: 'Standard English Conventions', level: 3.3 },
    ],
  },
];
const MAX_LEVEL = 7;
const SPLIT_AT = 680; // tile width at which the two cards sit side by side

// unscaled ms (the CSS multiplies by --cxs-slow)
const T_PICK = 1100;
const T_NEXT = T_PICK + 650;
const T_LINK = T_NEXT + 450;
const T_BARS = T_LINK + 450;
const BAR_STEP = 110;
const T_WEAK = T_BARS + 8 * BAR_STEP + 500;
// the beats, for a `results` card that keeps time with the question
export const ASSESS_TIMES = {
  pick: T_PICK, next: T_NEXT, link: T_LINK, bars: T_BARS, barStep: BAR_STEP, weak: T_WEAK,
};

function offsetIn(el, root) {
  let x = 0;
  let y = 0;
  let n = el;
  while (n && n !== root) {
    x += n.offsetLeft;
    y += n.offsetTop;
    n = n.offsetParent;
  }
  return n === root ? { x, y } : null;
}

function QuestionCard({ pickRef }) {
  return (
    <div className="cx-surface cxf-q cxs-in-panel">
      <div className="cx-window-bar cxf-bar">
        <span>Question 7</span>
        <span className="cxf-bar-meta">Advanced Math</span>
      </div>
      <div className="cxf-q-body">
        <div className="cxf-q-stem">
          f(x) = x<sup>2</sup> {MINUS} 4x
        </div>
        <div className="cxf-q-ask">What is the value of f(5)?</div>
        <ul className="cxf-q-choices">
          {CHOICES.map((c) => (
            <li
              key={c.key}
              ref={c.picked ? pickRef : undefined}
              className={`cxf-q-choice${c.picked ? ' is-picked cxs-in-press' : ''}`}
              style={c.picked ? { '--d': `${T_PICK}ms` } : undefined}
            >
              {c.picked ? <span className="cxf-q-ring cxs-in-fade" style={{ '--d': `${T_PICK}ms` }} /> : null}
              <span className="cxf-q-key">
                {c.key}
                {c.picked ? <span className="cxf-q-keyfill cxs-in-fade" style={{ '--d': `${T_PICK}ms` }}>{c.key}</span> : null}
              </span>
              <span className="cxf-q-val">{c.value}</span>
            </li>
          ))}
        </ul>
        <div className="cxf-q-foot">
          <span className="cx-action cxs-in-press" style={{ '--d': `${T_NEXT}ms` }}>Next</span>
        </div>
      </div>
    </div>
  );
}

function ResultsCard({ cardRef, weakRef }) {
  let k = 0;
  return (
    <div ref={cardRef} className="cx-surface cxf-r cxs-in-panel" style={{ '--d': '140ms' }}>
      <div className="cx-window-bar cxf-bar">
        <span>Class results</span>
        <span className="cxf-bar-meta">Average level, 1 to 7</span>
      </div>
      {GROUPS.map((g) => (
        <div key={g.name} className="cxf-r-group">
          <div className="cxf-r-glabel">{g.name}</div>
          <ul className="cxf-r-rows">
            {g.rows.map((r) => {
              const d = T_BARS + k * BAR_STEP;
              k += 1;
              return (
                <li key={r.name} ref={r.weak ? weakRef : undefined} className={`cxf-r-row${r.weak ? ' is-weak' : ''}`}>
                  {r.weak ? <span className="cxf-r-hl cxs-in-fade" style={{ '--d': `${T_WEAK}ms` }} /> : null}
                  <span className="cxf-r-line">
                    <span className="cxf-r-name">{r.name}</span>
                    {r.weak ? <span className="cxf-r-tag cxs-in-pop" style={{ '--d': `${T_WEAK + 120}ms` }}>Lowest</span> : null}
                    <span className="cxf-r-val cxs-in-fade" style={{ '--d': `${d + 300}ms` }}>{r.level.toFixed(1)}</span>
                  </span>
                  <span className="cxf-r-track">
                    <span className="cxf-r-fill" style={{ width: `${(r.level / MAX_LEVEL) * 100}%`, '--d': `${d}ms` }} />
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* `header` (schools preview, 2026-09-30): optional FeatureHeader overrides
   (title, sub, side, id, as, labelled, after, className added to ours) so the
   schools page can reuse this scene under its own words. With no props the
   teacher page renders exactly as before.
   Schools round 3 (2026-10-01), all optional, the defaults are the teacher
   page's: `results` swaps the right card (it gets cardRef + weakRef, so the
   connector lands on its weakRef row); `splitAt` the tile width for side by
   side; `variant` one class on the tile; `still` forces the final frame
   (the schools page's pause switch and live reduced motion); `sr` replaces
   the screen-reader sentence. */
export default function AssessmentsSection({
  header = {}, results: Results = ResultsCard, splitAt = SPLIT_AT, variant, still = false, sr,
}) {
  const {
    ref, reveal: revealRaw, live, near,
  } = useReveal({ amount: 0.35 });
  const reveal = still ? 'done' : revealRaw;
  const tileRef = useRef(null);
  const pickRef = useRef(null);
  const weakRef = useRef(null);
  const qRef = useRef(null);
  const rRef = useRef(null);
  const [split, setSplit] = useState(true);
  const [link, setLink] = useState(null);

  // the tile's own width decides the layout (it is full width when the
  // feature row stacks, the visual column when it does not)
  const measure = useCallback(() => {
    const tile = tileRef.current;
    if (!tile) return;
    const wide = tile.clientWidth >= splitAt;
    setSplit((s) => (s === wide ? s : wide));
    const pick = pickRef.current;
    const weak = weakRef.current;
    const q = qRef.current;
    const r = rRef.current;
    if (!wide || !pick || !weak || !q || !r) {
      setLink(null);
      return;
    }
    const p = offsetIn(pick, tile);
    const w = offsetIn(weak, tile);
    const qo = offsetIn(q, tile);
    const ro = offsetIn(r, tile);
    if (!p || !w || !qo || !ro) return;
    // from the question card's right edge, on the picked choice's center
    // line, to the results card's left edge, on the Advanced Math row's:
    // one flat run when they align (they do by design), else a square elbow
    const sx = qo.x + q.offsetWidth;
    const sy = Math.round(p.y + pick.offsetHeight / 2) + 0.5;
    const ex = ro.x;
    let ey = Math.round(w.y + weak.offsetHeight / 2) + 0.5;
    if (Math.abs(ey - sy) < 1.5) ey = sy;
    const mx = Math.round((sx + ex) / 2) + 0.5;
    const d = ey === sy ? `M${sx} ${sy} H${ex}` : `M${sx} ${sy} H${mx} V${ey} H${ex}`;
    const next = { sx, sy, ex, ey, d };
    setLink((l) => (l && l.d === next.d ? l : next));
  }, [splitAt]);

  useLayoutEffect(() => { measure(); }, [measure, split]);

  useEffect(() => {
    const tile = tileRef.current;
    if (!tile) return undefined;
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(tile);
    }
    let alive = true;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (alive) measure(); });
    return () => {
      alive = false;
      if (ro) ro.disconnect();
    };
  }, [measure]);

  return (
    <FeatureHeader
      n={2}
      title={(
        <>
          <span>Assessments</span>
          {' '}
          <span className="cxs-h2-tail">built for the SAT.</span>
        </>
      )}
      // no-break spaces keep "Reading & Writing" on one line
      sub={'Our assessments test each student on SAT Math and Reading & Writing skills, then place them on a level in every domain.'}
      side="right"
      id="cxs-f2"
      {...header}
      className={`cxf-f2${header.className ? ` ${header.className}` : ''}`}
    >
      <div ref={ref} className="cxf-assess-stage" data-reveal={reveal} data-live={live ? 'true' : 'false'}>
        {/* the soft mint mesh (w2, the owner's favorite), cropped from the top
            so its deep blue bottom band stays mostly out of the tile */}
        <Tile ref={tileRef} wall="w2" near={near} pos="50% 0%" className={`cxf-assess-tile ${split ? 'is-split' : 'is-stack'}${variant ? ` ${variant}` : ''}`} aria-hidden="true">
          {/* Buffy pokes out from behind the left side, behind the wallpaper (BuffyPeek.jsx) */}
          <BuffyPeek edge="left" reveal={reveal} paused={!live || still} />
          <span className="cxf-wash" />
          <div className="cxf-assess-grid">
            <div ref={qRef} className="cxf-q-wrap">
              <QuestionCard pickRef={pickRef} />
            </div>
            <Results cardRef={rRef} weakRef={weakRef} />
          </div>
          {link ? (
            <svg className="cxs-links cxf-links" aria-hidden="true" focusable="false">
              <g className="cxs-link" style={{ '--d': `${T_LINK}ms` }}>
                <path className="cxs-link-line" d={link.d} pathLength="1" />
                <circle className="cxs-link-dot cxf-link-dot" cx={link.sx} cy={link.sy} r="3.5" style={{ '--d': `${T_LINK}ms` }} />
                <circle className="cxs-link-dot cxf-link-dot" cx={link.ex} cy={link.ey} r="3.5" style={{ '--d': `${T_LINK + 380}ms` }} />
              </g>
            </svg>
          ) : null}
        </Tile>
        <p className="cxs-sr">
          {sr || (
            <>
              Sample: a student answers an Advanced Math question and picks a wrong answer. The class results show the class&apos;s
              average level, from 1 to 7, in each of the 8 SAT domains. Advanced Math is the lowest, at 2.8.
            </>
          )}
        </p>
      </div>
    </FeatureHeader>
  );
}
